<template>
  <div v-if="!doneLoading" class="d-flex justify-center mt-16">
    <WaitLoader></WaitLoader>
  </div>

  <v-app v-show="doneLoading">
    <v-app-bar v-if="!hideAppBarsAndDrawer"
               elevation="2"
               extension-height="30"
               :class="isAndroid ? 'pt-2' : ''">

      <template v-slot:prepend>
        <v-btn icon="mdi-menu" @click="drawer = !drawer"></v-btn>
        <a href="/chat/new" class="mt-1">
          <img v-if="!(currentActivity && xs) && showAppBarLogo && !isDarkMode" :src="kinectin" alt="kinectin logo" class="knLogo"/>
          <img v-if="!(currentActivity && xs) && showAppBarLogo && isDarkMode" :src="kinectinDm" alt="kinectin logo" class="knLogo"/>
          <img v-if="!(currentActivity && xs) && !showAppBarLogo && !isDarkMode && !xs" :src="kinectinLogo" alt="kinectin logo" class="knLogoSm"/>
          <img v-if="!(currentActivity && xs) && !showAppBarLogo && isDarkMode && !xs" :src="kinectinLogoDm" alt="kinectin logo" class="knLogoSm"/>
        </a>
      </template>

      <template v-slot:append>
        <v-menu v-if="org" location="bottom end">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="mr-1 cursor-pointer">
              <OrgAvatar :org="org" icon-only circle-size="30"/>
            </div>
          </template>
          <v-list density="compact">
            <v-list-item prepend-icon="mdi-home" title="Org Home" to="/org/home"></v-list-item>
            <v-list-item prepend-icon="mdi-chart-line-variant"
                         title="My Dashboard"
                         to="/org/dashboard"
            ></v-list-item>
            <v-list-item v-if="isTeamLead"
                         prepend-icon="mdi-chart-line"
                         title="Leadership Dashboard"
                         :to="`/org/${idToLinkId({ id: userProfile.orgId })}?tab=dashboardTab`"
            ></v-list-item>
          </v-list>
        </v-menu>
      </template>

      <v-container v-if="onChatPage" :style="`margin-left: ${(drawer && !xs)?100:0}px`">
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center">

              <div v-if="!onNewChatPage && !currentActivity">
                <img v-if="!isDarkMode && xs"
                     :src="kinectinLogo"
                     @click.prevent="handleNewChat()"
                     alt="kinectin logo"
                     class="knLogoXs cursor-pointer"/>

                <img v-if="isDarkMode && xs"
                     :src="kinectinLogoDm"
                     @click.prevent="handleNewChat()"
                     alt="kinectin logo"
                     class="knLogoXs cursor-pointer"/>

                <Avatar to="/profile"
                        class="mr-2"
                        color="primary"
                        icon-only
                        circle-size="30"
                        :image="profileImageUrl(userProfile)"></Avatar>

                <Avatar v-for="rel in selectedRelationships"
                        :to="`/rel/${idToLinkId({ id: rel.relationshipId })}`"
                        :key="rel.relationshipId"
                        :first-name="rel.nickName || rel.firstName"
                        :last-name="rel.lastName"
                        :image="profileImageUrl(rel)"
                        initials-only
                        initials-font-size="13px"
                        color="secondary"
                        class="mr-2"
                        circle-size="30">
                </Avatar>

                <Avatar v-if="selectedRelationships.length < 2"
                        color="secondary"
                        icon-only
                        icon="mdi-plus"
                        circle-size="30"
                        class="mr-2 cursor-pointer"
                        @click="eventBus.spa.emit('show-add-rel-dialog')"></Avatar>
              </div>
            </div>

            <div v-if="!onNewChatPage && selectedThread && !currentActivity"
                 :class="'text-body opacity-70' + (xs?' smallScreenWidth':'')">
              <span class="truncate">{{ selectedThread.name }}</span>
            </div>

            <div v-if="currentActivity"
                 class="d-flex align-center"
                 :class="'text-body opacity-70' + (xs?' smallScreenWidth':'')">

              <img v-if="!isDarkMode && xs"
                   :src="kinectinLogo"
                   @click.prevent="handleNewChat()"
                   alt="kinectin logo"
                   class="knLogoXs cursor-pointer"/>

              <img v-if="isDarkMode && xs"
                   :src="kinectinLogoDm"
                   @click.prevent="handleNewChat()"
                   alt="kinectin logo"
                   class="knLogoXs cursor-pointer"/>

              <v-btn to="/activities?tab=myActivitiesTab"
                     icon="mdi-chevron-left"></v-btn>
              <span class="truncate">{{ currentActivity.name }}</span>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <template v-slot:extension v-if="onChatPage && currentActivity">
        <div class="w-100 d-flex justify-center"
             :style="`margin-top: -15px; margin-left: ${(drawer && !xs)?300:0}px`">

          <v-progress-linear :model-value="selectedThread?.activityStepsProgress || 0"
                             :max="selectedThread?.activityStepsMax || 0"
                             height="15"
                             class="w-75 mb-2"
                             color="#4D49D3"></v-progress-linear>
        </div>
      </template>
    </v-app-bar>

    <v-navigation-drawer v-if="!hideAppBarsAndDrawer"
                         v-model="drawer"
                         :permanent="!smAndDown"
                         outline
                         width="300"
                         location="left">
      <v-list>
        <v-list-item prepend-icon="mdi-compass" title="Explore" to="/tools"></v-list-item>
        <v-list-item prepend-icon="mdi-message-plus-outline"
                     title="New Chat"
                     @click.prevent="handleNewChat({ reset: true })"></v-list-item>
        <v-list-item title="Session History" to="/sessions">
          <template v-slot:prepend>
            <v-badge color="error" :content="openThreadMarkers.length" :model-value="!!openThreadMarkers.length"
                     max="99">
              <v-icon icon="mdi-message-text"></v-icon>
            </v-badge>
          </template>
        </v-list-item>
        <v-list-item prepend-icon="mdi-account-multiple" title="Relationships" to="/relationships"></v-list-item>
        <v-list-item to="/profile">
          <Avatar :image="profileImageUrl(userProfile)"
                  to="/profile"
                  color="primary"
                  icon-only
                  style="margin-right: 22px"
                  circle-size="30"></Avatar>
          Well-Being Hub
        </v-list-item>
        <v-list-item prepend-icon="mdi-handshake" title="My Support Team" to="/supportteam"/>
        <v-list-item prepend-icon="mdi-credit-card" title="Billing" to="/billing"/>
        <v-divider></v-divider>
        <template v-if="store.userCanManageOrgIds()">
          <v-list-item prepend-icon="mdi-office-building" title="Manage Orgs" to="/orgs"/>
          <v-divider></v-divider>
        </template>
        <v-list-item prepend-icon="mdi-lock" title="Privacy" to="/privacy"/>
        <v-list-item prepend-icon="mdi-gavel" title="Terms & Conditions" to="/terms"/>
        <v-list-item prepend-icon="mdi-information" title="About" to="/about"/>
        <v-list-item prepend-icon="mdi-message-alert" title="Feedback"
                     @click="showFeedbackDialog = !showFeedbackDialog"/>
        <v-divider></v-divider>
        <v-list-item prepend-icon="mdi-cog" title="Account" to="/account"/>
        <v-divider></v-divider>
        <v-list-item prepend-icon="mdi-account-outline" title="Sign Out" @click="doSignOut"/>
        <v-divider></v-divider>
        <template v-if="store.userHasPrivilege(Privileges.diagnostics)">
          <v-list-item prepend-icon="mdi-wrench" title="Diagnostics" to="/diagnostics"/>
        </template>
        <template v-if="store.userCanManageUsers()">
          <v-list-item prepend-icon="mdi-account-hard-hat" title="Manage Users" to="/users"/>
          <v-list-item prepend-icon="mdi-wrench-cog" title="Manage Settings" to="/settings"/>
        </template>
      </v-list>
      <OrgAvatar :org="org"></OrgAvatar>
    </v-navigation-drawer>

    <RelationshipDialog :relationship="relationship"
                        :show-dialog="showRelationshipDialog"
                        @update:show-dialog="show => showRelationshipDialog = show"></RelationshipDialog>

    <FeedbackDialog v-model="showFeedbackDialog"></FeedbackDialog>

    <v-dialog v-model="showAddRelationshipDialog" max-width="600">
      <v-card class="pa-5">
        <v-card-title>Add Relationship</v-card-title>
        <v-card-subtitle>Add relational context for Amari in this conversation</v-card-subtitle>

        <v-card-text v-if="!addableRels.length">
          <div v-if="store.canAddRelationships">
            You either need to
            <v-btn prepend-icon="mdi-eye"
                   variant="tonal"
                   size="small"
                   @click="showHiddenRels = true; showAddRelationshipDialog = false">
              show hidden
            </v-btn>
            relationships and unhide one, or
            <v-btn prepend-icon="mdi-account"
                   variant="tonal"
                   color="primary"
                   size="small"
                   @click="handleRelAdd()">Add
            </v-btn>
            a new one.
          </div>
          <span v-if="!store.canAddRelationships">
            It looks like you need to unhide a relationship.  Use the left bar to "Show Hidden".
          </span>
        </v-card-text>
        <v-card-text v-if="addableRels.length">
          <v-select v-model="relToAdd"
                    :items="addableRels"
                    label="Relationship"
                    variant="outlined"
                    density="compact"
                    class="mt-5"
                    max-width="300"
          ></v-select>
        </v-card-text>

        <v-card-actions>
          <v-btn variant="text"
                 color="secondary"
                 @click="handleAddRelToChat"
                 :disabled="!relToAdd"
                 :loading="addingRelToChat">
            Submit
          </v-btn>
          <v-btn @click="showAddRelationshipDialog = false" :disabled="addingRelToChat">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPersonalModeReminder"
              max-width="500"
              persistent
              :content-class="drawer && !xs ? 'anon-dialog-offset' : ''">
      <v-card rounded="xl" class="pa-2">
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="me-2">mdi-shield-lock</v-icon>
          Personal Mode
        </v-card-title>
        <v-card-text class="text-body-1">
          You are now in personal mode. Everything you discuss here is protected.
          {{ org?.name || 'Your organization' }} will never see anything discussed
          nor any activity in personal mode.
        </v-card-text>
        <v-card-actions class="flex-column align-start px-4">
          <v-checkbox v-model="dontShowPersonalModeAgain"
                      label="Don't show this again"
                      hide-details
                      density="compact" />
          <div class="w-100 d-flex justify-end mt-2">
            <v-btn color="primary" variant="flat" rounded="lg" @click="dismissPersonalModeReminder">
              Got it
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="10000">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-bottom-navigation app v-if="!hideAppBarsAndDrawer" height="70">
      <v-btn :active="false" to="/tools" stacked size="small" class="text-none px-1">
        <v-icon color="primary">mdi-compass</v-icon>
        <span class="text-caption text-primary">Explore</span>
      </v-btn>
      <v-btn :active="false" @click.prevent="handleNewChat({ reset: true })" stacked size="small" class="text-none px-1">
        <v-icon color="primary">mdi-message-plus-outline</v-icon>
        <span class="text-caption text-primary">New Chat</span>
      </v-btn>
      <v-btn :active="false" to="/sessions" id="historyTab" stacked size="small" class="text-none px-1">
        <v-badge color="error"
                 offset-x="-5"
                 :content="openThreadMarkers.length"
                 :model-value="!!openThreadMarkers.length"
                 max="99">
          <v-icon color="primary">mdi-message-text</v-icon>
        </v-badge>
        <span class="text-caption text-primary">History</span>
      </v-btn>
      <v-btn :active="false" to="/relationships" id="relsTab" stacked size="small" class="text-none px-1">
        <v-icon color="primary">mdi-account-multiple</v-icon>
        <span class="text-caption text-primary">Relationships</span>
      </v-btn>
      <v-btn :active="false" to="/profile" id="profileTab" stacked size="small" class="text-none px-1">
        <Avatar :image="profileImageUrl(userProfile)"
                to="/profile"
                color="primary"
                icon-only
                circle-size="23"></Avatar>
        <span class="text-caption text-primary">Hub</span>
      </v-btn>
    </v-bottom-navigation>

    <v-main
        :class="`page overflow-hidden px-0 ${bgClass} ${ showBgImage ? '' : 'py-16' }`"
        :style="{ marginLeft: (!hideAppBarsAndDrawer && drawer && !xs) ? '300px' : '', ...orgBgStyle }">

      <div v-show="!(isOrgPage && orgThemeColor)" class="bg" :style="{ backgroundImage: `url(${isDarkMode ? bgImageDm : bgImage})` }"/>

      <div class="content">
        <v-slide-y-transition>
          <v-banner v-if="newUiVersionDetected"
                    elevation="1"
                    color="warning"
                    icon="mdi-update"
                    lines="one"
                    class="kn-fixbg">
            <template v-slot:text>New Updates Available!</template>
            <template v-slot:actions>
              <v-btn @click="handleReload()">Reload</v-btn>
            </template>
          </v-banner>
        </v-slide-y-transition>

        <v-slide-y-transition leave-absolute>
          <v-banner v-if="showNoTokensBanner && !showNoTokensBannerDismiss"
                    elevation="1"
                    color="error"
                    icon="mdi-credit-card"
                    lines="one"
                    class="kn-fixbg">
            <template v-slot:text>Replenish Tokens to Chat!</template>
            <template v-slot:actions>
              <div class="align-center">
                <v-btn to="/billing" size="small">Billing</v-btn>
                <v-btn icon="mdi-close" size="small" color="" @click="showNoTokensBannerDismiss = true"></v-btn>
              </div>
            </template>
          </v-banner>
        </v-slide-y-transition>

        <v-slide-y-transition leave-absolute>
          <v-banner v-if="daysRemainingInPlan && !daysRemainingInPlanDismiss"
                    elevation="1"
                    color="warning"
                    icon="mdi-credit-card"
                    lines="one"
                    class="kn-fixbg">
            <template v-slot:text>Your {{ inFreeTrialNow ? 'free trial' : 'plan' }} expires in {{ daysRemainingInPlan }}
              days.
            </template>
            <template v-slot:actions>
              <div class="align-center">
                <v-btn to="/billing" size="small">Billing</v-btn>
                <v-btn icon="mdi-close" size="small" color="" @click="daysRemainingInPlanDismiss = true"></v-btn>
              </div>
            </template>
          </v-banner>
        </v-slide-y-transition>

        <router-view></router-view>
      </div>
    </v-main>

    <SpotlightOverlay :selections="highlightedSelections"/>

    <v-menu v-if="showBottomQ" location="top">
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" fab size="small" color="primary"
               icon="mdi-help" class="ma-5" location="bottom left"></v-btn>
      </template>
      <v-list>
        <v-list-item key="0" density="compact" prepend-icon="mdi-home" to="/" title="App Home"></v-list-item>
        <v-list-item key="1" density="compact" prepend-icon="mdi-home-outline" href="https://kinectin.com"
                     target="_blank" title="Kinectin.com"></v-list-item>
        <v-list-item key="2" density="compact" prepend-icon="mdi-lock" to="/privacy" title="Privacy"></v-list-item>
        <v-list-item key="3" density="compact" prepend-icon="mdi-gavel" to="/terms"
                     title="Terms & Conditions"></v-list-item>
        <v-list-item key="4" density="compact" prepend-icon="mdi-information" to="/about" title="About"></v-list-item>
      </v-list>
    </v-menu>
  </v-app>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import eventBus from '@/service/EventBus.js'
import pushNotifications from '@/service/pushNotifications.js'
import { doSignOut, getThemeForVuetify, getUserSession } from '@/service/UserService.js'
import { nativeAppHideDrawerRoutes, webAppHideDrawerRoutes, unauthenticatedRoutes } from '@/middleware/routeGuard.js'
import { useKinectinStore } from '@/store/kinectin.js'
import { storeToRefs } from 'pinia'
import FeedbackDialog from '@/components/FeedbackDialog.vue'
import RelationshipDialog from '@/components/RelationshipDialog.vue'
import { differenceBy, isEmpty } from 'lodash-es'
import Avatar from '@/components/Avatar.vue'
import kinectin from '@/assets/kinectin.png'
import kinectinDm from '@/assets/kinectin-dm.png'
import kinectinLogo from '@/assets/kinectin-logo.png'
import kinectinLogoDm from '@/assets/kinectin-logo-dm.png'
import favicon from '@/assets/favicon.ico'
import faviconDm from '@/assets/favicon-dm.ico'
import {
  idToLinkId,
  profileImageUrl,
  setNativeAppStatusBarPurple,
  setNativeAppStatusBarColor,
  orgBgCssVars,
} from '@/service/uiUtils.js'
import SpotlightOverlay from '@/components/SpotlightOverlay.vue'
import { Privileges } from '../../api/common/sharedUtils.js'
import { DateTime } from 'luxon'
import OrgAvatar from '@/components/OrgAvatar.vue'
import WaitLoader from '@/components/WaitLoader.vue'
import bgImage from '@/assets/kinectin-bg.jpg'
import bgImageDm from '@/assets/kinectin-bg-dm.jpg'
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import privateStorage from '@/store/privateStorage.js'

const store = useKinectinStore()
const {
  maintenanceMode,
  userIsAuthenticated,
  userProfile,
  openThreadMarkers,
  userRelationships,
  selectedThread,
  selectedRelationships,
  revCatInfo,
  currentSubscription,
  tokenUsage,
  chatStartPrompt,
  drawer,
  currentActivity,
  orgThemeColor,
} = storeToRefs(store)

const { smAndDown, mdAndUp, xs } = useDisplay()
drawer.value = mdAndUp.value

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('')
const routesThatDontUseAppBarOrDrawer = Capacitor.isNativePlatform()
    ? nativeAppHideDrawerRoutes
    : webAppHideDrawerRoutes
const route = useRoute()
const router = useRouter()
const doneLoading = ref(false)
const theme = useTheme()
const showFeedbackDialog = ref(false)
const showRelationshipDialog = ref(false)
const showAddRelationshipDialog = ref(false)
const showHiddenRels = ref(false)
const relToAdd = ref(null)
const addingRelToChat = ref(false)
const relationship = ref(null)
const showBottomQ = ref(true)
const newUiVersionDetected = ref(false)
const highlightedSelections = ref([])
const org = ref(null)
const showPersonalModeReminder = ref(false)
const dontShowPersonalModeAgain = ref(false)

const isTeamLead = computed(() => {
  const orgId = userProfile.value?.orgId
  if (!orgId) return false
  const tags = userProfile.value?.tags || []
  return tags.includes(`${orgId}:role:TeamLead`)
})

const isDarkMode = computed(() => theme.global.name.value === 'dark')
const isAndroid = computed(() => Capacitor.getPlatform() === 'android')

function updateFavicon() {
  const fav = document.head.querySelector(`link[rel='icon']`)
  fav.href = isDarkMode.value ? faviconDm : favicon
}

watch(isDarkMode, updateFavicon)

const baselineOnboarding = computed(() => route.name === 'assessments-baseline' && route.query.onboarding)

const onboarding = computed(() => (['onboard'].includes(route.name)))

const showBgImage = computed(
    () => (
        onboarding || baselineOnboarding.value ||
        ['signin', 'signup', 'signupwith-orgUrlSegment', 'signinwith-orgUrlSegment'].includes(route.name)
    ),
)

const isOrgPage = computed(() =>
    route.name?.startsWith('org-') ||
    (route.name === 'screeners-screener' && route.query.orgId),
)

const orgBgStyle = computed(() => isOrgPage.value ? orgBgCssVars(orgThemeColor.value) : {})

watch(isOrgPage, async (newVal, oldVal) => {
  if (oldVal && !newVal && org.value) {
    const dismissed = await privateStorage.getItem('kn.personalModeDismissed')
    if (!dismissed) showPersonalModeReminder.value = true
  }
})

async function dismissPersonalModeReminder() {
  showPersonalModeReminder.value = false
  if (dontShowPersonalModeAgain.value) {
    await privateStorage.setItem('kn.personalModeDismissed', 'true')
  }
}

const bgClass = computed(() => {
    if (currentActivity.value || ['activities', 'activities-view_activity'].includes(route.name)) {
        return 'activityBg'
    }
    if (isOrgPage.value && orgThemeColor.value) {
        return 'orgBg'
    }
    if (onChatPage.value || ['tools', 'profile', 'assessments-dailyjournal'].includes(route.name)) {
        return 'defaultBg'
    }
    return ''
})

watch(showBgImage, async () => {
  if (showBgImage.value) {
    await setNativeAppStatusBarPurple() // because the signin/signup pages have a dark top
  } else {
    await setNativeAppStatusBarColor()
  }
})

const showAppBarLogo = computed(
    () => (route.name !== 'chat-threadId') ||
        (route.path === '/chat/new' || !smAndDown.value) ||
        (drawer.value && !xs.value),
)

const inFreeTrialNow = computed(
    () => {
      const periodStart = revCatInfo.value?.periodStart
      const periodEnd = revCatInfo.value?.periodEnd
      if (!periodStart || !periodEnd) return false

      const start = DateTime.fromISO(periodStart)
      const end = DateTime.fromISO(periodEnd)
      const periodDays = end.diff(start, 'days').days.toFixed()
      const usageDays = end.diff(DateTime.now(), 'days').days.toFixed()
      const usageDaysWithinRange = 1 <= usageDays && usageDays <= 7
      const periodDaysWithinRange = 1 <= periodDays && periodDays <= 7
      return usageDaysWithinRange && periodDaysWithinRange
    },
)

const daysRemainingInPlanDismiss = ref(false)
const daysRemainingInPlan = computed(
    () => {
      const periodEnd = revCatInfo.value?.periodEnd
      const cancelAtPeriodEnd = revCatInfo.value?.status === 'canceled'
      if (!periodEnd) return null
      if (showNoTokensBanner.value) return null // no need to show days remaining banner if no tokens left

      const end = DateTime.fromISO(periodEnd)
      const days = end.diff(DateTime.now(), 'days').days.toFixed()
      const daysWithinRange = 1 <= days && days <= 7
      return daysWithinRange && (currentSubscription.value.billingInterval === 'year' || cancelAtPeriodEnd) ? days : null
    },
)

const showNoTokensBannerDismiss = ref(false)
const showNoTokensBanner = computed(() =>
    !store.userHasPrivilege(Privileges.unlimitedTokens) &&
    (onChatPage.value || onNewChatPage.value) &&
    tokenUsage.value.monthlyTokens === 0,
)

const addableRels = computed(() =>
    differenceBy(userRelationships.value, selectedRelationships.value, 'relationshipId')
        .filter(e => !e.hide || showHiddenRels.value)
        .map(e => ({ value: e, title: e.firstName + ' ' + e.lastName })))

const onChatPage = computed(() => route.name === 'chat-threadId')
const onNewChatPage = computed(() => route.path === '/chat/new')
const routerReady = ref(false)

const hideAppBarsAndDrawer = computed(
    () =>
        !routerReady.value ||
        route.query.onboarding ||
        baselineOnboarding.value ||
        routesThatDontUseAppBarOrDrawer.includes(route.name),
)

watch(() => route.path, newPath => {
  if (unauthenticatedRoutes.includes(route.name) && !doneLoading.value) {
    doneLoading.value = true
  }
  showBottomQ.value = route.path !== '/sessions'
})

watch(userIsAuthenticated, newValue => {
  if (userIsAuthenticated.value && !doneLoading.value) {
    doneLoading.value = true
  }
})

watch(showAddRelationshipDialog, newValue => {
  if (showAddRelationshipDialog.value) {
    relToAdd.value = addableRels.value[0]?.value
  }
})

async function handleReload() {
  window.location.reload()
}

async function handleNewChat({ rel, prompt, reset } = {}) {
  selectedRelationships.value = rel ? [rel] : []
  chatStartPrompt.value = prompt
  relToAdd.value = null
  const query = {}
  if (rel) query.relc = '1'
  if (reset) query.reset = Date.now().toString()
  const to = { path: '/chat/new', query }
  await router.push(to)
}

async function handleAddRelToChat() {
  selectedRelationships.value.push(relToAdd.value)
  showAddRelationshipDialog.value = false

  // Track explicitly added relationship
  await pushNotifications.track('Chat Session - Relationship Added')

  const thread = selectedThread.value
  if (thread) {
    const existingRelIds = thread.relationshipIds || []
    const newRelId = relToAdd.value.relationshipId
    const attrs = { relationshipIds: [...existingRelIds, newRelId] }
    await store.updateThread({ threadId: thread.threadId, attrs })
    eventBus.spa.emit('add-relationship-to-chat', { relationshipId: newRelId })
  }
  relToAdd.value = null
}

async function handleRelAdd() {
  relationship.value = null
  showRelationshipDialog.value = true
  showAddRelationshipDialog.value = false
}

async function init() {
  router.isReady().then(() => routerReady.value = true)

  const { session, token, userId } = await getUserSession()
  if (session && token && userId) {
    await Promise.all([
      store.loadUserProfile(),
      store.loadOpenThreadMarkers(),
      store.loadTokenUsage(),
      store.loadUserRelationships(),
      store.loadUserRevCatInfo(), // for computing store.canAddRelationships
    ])
    theme.global.name.value = getThemeForVuetify({ theme: userProfile.value.theme })
  }
  updateFavicon()
}

watch(userProfile, async () => {
  if (!isEmpty(userProfile.value.orgId)) {
    org.value = await store.getOrgPublicProfile({ orgId: userProfile.value.orgId })
  }
})

onMounted(async () => {
  eventBus.spa.on('show-snackbar', handleShowSnackbar)
  eventBus.spa.on('user-auth', init)
  eventBus.spa.on('new-chat', handleNewChat)
  eventBus.spa.on('show-add-rel-dialog', handleShowAddRelDialog)
  eventBus.spa.on('new-ui-version-detected', handleNewUiVersionDetected)
  eventBus.spa.on('maintenance-mode', handleMaintenanceMode)
  eventBus.spa.on('spotlight-element', handleSpotlightElement)
  await init()
})

onBeforeUnmount(() => {
  eventBus.spa.off('show-snackbar', handleShowSnackbar)
  eventBus.spa.off('user-auth', init)
  eventBus.spa.off('new-chat', handleNewChat)
  eventBus.spa.off('show-add-rel-dialog', handleShowAddRelDialog)
  eventBus.spa.off('new-ui-version-detected', handleNewUiVersionDetected)
  eventBus.spa.off('maintenance-mode', handleMaintenanceMode)
  eventBus.spa.off('spotlight-element', handleSpotlightElement)
})

function handleSpotlightElement({ selections }) {
  highlightedSelections.value = selections
}

function handleNewUiVersionDetected() {
  newUiVersionDetected.value = true
}

function handleMaintenanceMode(enabled) {
  maintenanceMode.value = enabled
}

function handleShowAddRelDialog() {
  showAddRelationshipDialog.value = true
}

function handleShowSnackbar(payload) {
  showSnackbar.value = true
  snackbarText.value = payload.text
  snackbarColor.value = payload.color || 'primary'
}

watch(maintenanceMode, (newValue, oldValue) => {
  if (newValue && !oldValue) router.push({ path: '/berightback', query: { redirect: route.fullPath } })
})
watch(smAndDown, (isSmaller) => {
  if (isSmaller) drawer.value = false
})
watch(mdAndUp, (isBigger) => {
  if (isBigger) drawer.value = true
})
watch(drawer, newValue => {
  if (!hideAppBarsAndDrawer.value) {
    if (Capacitor.isNativePlatform()) {
      Haptics.impact({ style: ImpactStyle.Light })
          .catch(err => console.warn('Haptics error', err))
    }
  }
})

// Call mixpanel tag. Override page name if default name is weird
router.afterEach((to) => {
  const pageName = to.name || to.path

  mixpanel.track(`${pageName} - Page Viewed`, {
    path: to.path,
    pageName,
    routeName: to.name,
    title: document.title,
  })
})


</script>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.smallScreenWidth {
  width: 240px;
}

.activeRelationship.v-theme--dark {
  background: rgba(255, 255, 255, 0.1);
}

.activeRelationship.v-theme--light {
  background: rgba(0, 0, 0, 0.1);
}

.v-theme--light.v-banner.kn-fixbg {
  background-color: rgba(230, 230, 230, 0.7) !important
}


.knLogo {
  width: 150px;
  height: auto;
}

.knLogoSm {
  width: 50px;
  height: auto;
  margin-left: 7px;
}

.knLogoXs {
  width: 35px;
  height: auto;
  margin-right: 4px;
}

.activityBg {
  background: linear-gradient(to bottom right, #e6f7f2, #b8ddd9);
}

.v-theme--dark .activityBg {
  background: linear-gradient(to bottom right, #51bfc6, #033540);
}

.defaultBg {
  background: linear-gradient(to bottom right, #ffffff, #e5e4ff);
}

.v-theme--dark .defaultBg {
  background: linear-gradient(to bottom right, #6f6bfa, #0a0686);
}

.orgBg {
  background: linear-gradient(to bottom right,
    var(--org-bg-light-start, #e6f7f4),
    var(--org-bg-light-end, #b3e8df));
}

.v-theme--dark .orgBg {
  background: linear-gradient(to bottom right,
    var(--org-bg-dark-start, #11BB9A),
    var(--org-bg-dark-end, #064d3e));
}

.page {
  position: relative;
}

.bg {
  position: fixed;
  inset: 0;
  z-index: 0;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  transform: translateZ(0); /* helps smoothness on iOS */
}

.content {
  position: relative;
  z-index: 1;
}

</style>

<style>
.anon-dialog-offset {
  transform: translateX(150px);
}
</style>
